import * as React from 'react';
import api from '../../../api';
import { useMasterData } from '../../hooks/useMasterData';
import classnames from 'classnames';
import { useOutsideClick } from '../../hooks';
import { TestDriveVehicle, VehicleVariant } from '../..';
import { DropdownList } from 'react-widgets';

type VehicleModelVariant = {
  modelCode: string;
  variantCode?: string;
  year: string;
};

interface ITestDriveVehicleModelVariantDropdownProps {
  placeholder?: string;
  searchPlaceholder?: string;
  default?: VehicleModelVariant;
  onChange?: (data: VehicleModelVariant) => void;
  disabled?: boolean;
  data: TestDriveVehicle[];
}

const TestDriveVehicleModelVariantDropdown: React.FunctionComponent<ITestDriveVehicleModelVariantDropdownProps> = (
  props
) => {
  // const masterData = useMasterData(api.settings);
  const [isOpen, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  useOutsideClick(ref, () => {
    setOpen(false);
    setSearch('');
  });

  const allVariants = props.data || [];
  const variants = search
    ? allVariants.filter(
        (vv) =>
          vv.name?.match(new RegExp(search, 'si')) ||
          vv.year?.match(new RegExp(search, 'si')) ||
          vv.vehicleModel?.name?.match(new RegExp(search, 'si')) ||
          vv.vehicleModel?.modelCode?.match(new RegExp(search, 'si'))
      )
    : allVariants;
  const modelCodes = [
    ...new Set(allVariants.map((vv) => vv.vehicleModel.modelCode)),
  ];

  React.useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);
  if (!props.data.length) {
    return (
      <DropdownList
        disabled={props?.disabled}
        data={[]}
        placeholder="Select"
        filter={'contains'}
      />
    );
  }

  if (!modelCodes.length) {
    return <></>;
  }

  const selectedVariants = allVariants.find(
    (vv) =>
      vv.vehicleVariant.variantCode === props.default?.variantCode &&
      vv.year === props.default?.year &&
      vv.vehicleModel?.modelCode === props.default?.modelCode
  );

  const selected = props.default
    ? {
        modelCode: selectedVariants?.vehicleModel?.modelCode,
        variantCode: selectedVariants?.vehicleVariant?.variantCode,
        year: selectedVariants?.vehicleModel?.year,
      }
    : undefined;

  const onChange = (modelCode: string, year: string, variantCode: string) => {
    setOpen(false);
    setSearch('');
    props.onChange?.({
      modelCode,
      year,
      variantCode,
    });
  };

  const getUniqueValues = (arr: TestDriveVehicle[]) => {
    return arr.filter(
      (ele, ind) =>
        ind ===
        arr.findIndex(
          (elem) =>
            elem.vehicleVariant?.variantCode === ele.vehicleVariant?.variantCode
        )
    );
  };

  return (
    <div className="custom-dropdown position-relative w-100" ref={ref}>
      <div
        className={classnames({
          'custom-dropdown__value border rounded-sm font-normal d-flex align-items-center font-weight-semibold  w-100': true,
          disabled: props.disabled,
          'cursor-pointer': !props.disabled,
        })}
        onClick={() => !props.disabled && setOpen(!isOpen)}
      >
        <div
          className={classnames({
            'flex-fill text-truncate custom-dropdown__value-text py-1': true,
            'text-gray-500': !selected,
            'text-gray-700': selected,
          })}
        >
          {selectedVariants?.vehicleVariant?.name ||
            props.placeholder ||
            'Select'}
        </div>
        <i className="icon-chevron-down ml-auto"></i>
      </div>
      {!props.disabled && isOpen && (
        <div
          className={classnames({
            'px-0 bg-white border custom-dropdown__menu left-0 overflow-auto position-absolute w-100 zIndex-1': true,
            'custom-dropdown__menu--open': isOpen,
          })}
        >
          <div className="custom-dropdown__search border bg-gray-100 d-flex mb-2 position-sticky top-0 zIndex-1 mx-2">
            <button
              type="button"
              className="bg-transparent border-0 px-2 text-gray-600 d-flex align-items-center"
            >
              <i className="icon-search"></i>
            </button>
            <input
              ref={inputRef}
              autoFocus
              type="text"
              className="form-control bg-transparent border-0 py-0 px-1 font-weight-semibold font-normal"
              placeholder={props.searchPlaceholder || 'Search'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {modelCodes.map((modelCode) => {
            const modelVariants = variants.filter(
              (vv) => vv.vehicleModel.modelCode === modelCode
            );
            const years = [...new Set(modelVariants.map((vv) => vv.year))];
            if (!years.length) {
              return <></>;
            }

            return (
              <>
                <div className="border-bottom">
                  <div className="custom-dropdown__menu-item d-flex align-items-center bg-gray-300 px-3 py-2">
                    <div className="font-normal font-weight-semibold">
                      {modelCode}
                    </div>
                  </div>
                  {years.map((year) => {
                    let filteredVariants = modelVariants.filter(
                      (vv) => vv.year === year
                    );
                    filteredVariants = getUniqueValues(filteredVariants);
                    if (!filteredVariants?.length) {
                      return <></>;
                    }

                    return (
                      <>
                        <div className="custom-dropdown__menu-item d-flex align-items-center bg-gray-100 px-3 py-2">
                          <div className="font-normal font-weight-semibold">
                            {year}
                          </div>
                        </div>
                        {filteredVariants.map((vv) => {
                          return (
                            <div
                              className="custom-dropdown__menu-item d-flex align-items-center cursor-pointer px-3 py-3"
                              onClick={() =>
                                onChange(
                                  modelCode,
                                  year,
                                  vv.vehicleVariant.variantCode
                                )
                              }
                            >
                              <div className="font-normal font-weight-semibold">
                                {vv.vehicleVariant?.name}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })}
                </div>
              </>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestDriveVehicleModelVariantDropdown;
